import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import AddServiceSubunitDialog from './AddServiceSubunitDialog';
import EditServiceSubunitDialog from './EditServiceSubunitDialog';
import api from '../utils/api';
import './ServiceSubunits.css';

const ServiceSubunits = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const [service, setService] = useState(location.state?.service || null);
  
  const [subunits, setSubunits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedSubunit, setSelectedSubunit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedSubunit, setDraggedSubunit] = useState(null);
  const [dragOverSubunit, setDragOverSubunit] = useState(null);
  const [isReordering, setIsReordering] = useState(false);

  const fetchSubunits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/service-subunits?administration_ref=${serviceId}`);
      
      if (response.data.success) {
        const sortedSubunits = response.data.data.sort((a, b) => {
          if (a.priority !== undefined && b.priority !== undefined) {
            return a.priority - b.priority;
          }
          return a.name.localeCompare(b.name);
        });
        setSubunits(sortedSubunits);
      } else {
        throw new Error(response.data.message || 'Failed to fetch subunits');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch subunits');
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  const fetchService = useCallback(async () => {
    try {
      const response = await api.get('/services');
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch service');
      }

      const matchedService = response.data.data.find(item => item._id === serviceId);
      if (!matchedService) {
        throw new Error('Service not found');
      }

      setService(matchedService);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch service');
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => {
    if (!service && serviceId) {
      fetchService();
    }
  }, [service, serviceId, fetchService]);

  useEffect(() => {
    if (serviceId) {
      fetchSubunits();
    }
  }, [serviceId, fetchSubunits]);

  const handleAddSubunit = async (subunitData) => {
    try {
      const data = {
        ...subunitData,
        administration_ref: serviceId
      };
      
      const response = await api.post('/service-subunits', data);
      
      if (response.data.success) {
        setSubunits(prevSubunits => {
          const updated = [...prevSubunits, response.data.data];
          return updated.sort((a, b) => {
            if (a.priority !== undefined && b.priority !== undefined) {
              return a.priority - b.priority;
            }
            return a.name.localeCompare(b.name);
          });
        });
        setShowAddDialog(false);
      } else {
        throw new Error(response.data.message || 'Failed to create subunit');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create subunit');
    }
  };

  const handleEditSubunit = async (id, subunitData) => {
    try {
      const response = await api.put(`/service-subunits/${id}`, subunitData);
      
      if (response.data.success) {
        setSubunits(prevSubunits => {
          const updated = prevSubunits.map(subunit => 
            subunit._id === id ? { ...subunit, ...response.data.data } : subunit
          );
          return updated.sort((a, b) => {
            if (a.priority !== undefined && b.priority !== undefined) {
              return a.priority - b.priority;
            }
            return a.name.localeCompare(b.name);
          });
        });
        setShowEditDialog(false);
        setSelectedSubunit(null);
      } else {
        throw new Error(response.data.message || 'Failed to update subunit');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update subunit');
    }
  };

  const handleDeleteSubunit = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subunit?')) {
      return;
    }

    try {
      const response = await api.delete(`/service-subunits/${id}`);
      
      if (response.data.success) {
        setSubunits(subunits.filter(subunit => subunit._id !== id));
      } else {
        throw new Error(response.data.message || 'Failed to delete subunit');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete subunit');
    }
  };

  const openEditDialog = (subunit) => {
    setSelectedSubunit(subunit);
    setShowEditDialog(true);
  };

  const openStaffs = (subunit) => {
    navigate(`/services/${serviceId}/subunits/${subunit._id}/staffs`, {
      state: { 
        subunit: subunit,
        service: service 
      }
    });
  };

  const sendUpdatedPriorities = useCallback(async (updatedSubunits) => {
    try {
      const priorities = updatedSubunits.map((subunit, index) => ({
        _id: subunit._id,
        priority: index + 1
      }));

      const response = await api.put('/service-subunits/reorder', {
        subunits: priorities
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to reorder subunits');
      }
    } catch (err) {
      console.error('Failed to update service subunit order:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save new order');
      fetchSubunits();
    } finally {
      setIsReordering(false);
    }
  }, [fetchSubunits]);

  const handleDragStart = (e, subunit) => {
    if (searchTerm.trim()) {
      return;
    }

    setDraggedSubunit(subunit);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
  };

  const handleDragOver = (e, subunit) => {
    if (searchTerm.trim()) {
      return;
    }

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedSubunit && draggedSubunit._id !== subunit._id) {
      setDragOverSubunit(subunit._id);
    }
  };

  const handleDrop = (e, targetSubunit) => {
    if (searchTerm.trim()) {
      return;
    }

    e.preventDefault();

    if (!draggedSubunit || draggedSubunit._id === targetSubunit._id) {
      setDraggedSubunit(null);
      setDragOverSubunit(null);
      return;
    }

    const draggedIndex = subunits.findIndex(subunit => subunit._id === draggedSubunit._id);
    const targetIndex = subunits.findIndex(subunit => subunit._id === targetSubunit._id);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    const newSubunits = [...subunits];
    const [removed] = newSubunits.splice(draggedIndex, 1);
    newSubunits.splice(targetIndex, 0, removed);

    const updatedSubunits = newSubunits.map((subunit, index) => ({
      ...subunit,
      priority: index + 1
    }));

    setSubunits(updatedSubunits);
    setDraggedSubunit(null);
    setDragOverSubunit(null);
    setIsReordering(true);
  };

  const handleDragEnd = () => {
    setDraggedSubunit(null);
    setDragOverSubunit(null);
  };

  const filteredSubunits = subunits.filter(subunit =>
    subunit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subunit.nameEn.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canReorder = !searchTerm.trim() && filteredSubunits.length > 1;

  useEffect(() => {
    if (isReordering) {
      const timeoutId = setTimeout(() => {
        sendUpdatedPriorities(subunits);
      }, 1000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isReordering, subunits, sendUpdatedPriorities]);

  if (!service && !loading) return null;

  if (loading) {
    return (
      <div className="subunits-container">
        <div className="loading-spinner">Loading subunits...</div>
      </div>
    );
  }

  return (
    <div className="subunits-container">
      <div className="subunits-header">
        <div className="header-left">
          <h2>Service Subunits - {service.name}</h2>
          {canReorder && (
            <p className="reorder-hint">Drag and drop halls to reorder them</p>
          )}
          {searchTerm.trim() && subunits.length > 1 && (
            <p className="search-hint">Clear search to reorder halls</p>
          )}
        </div>
        <button 
          className="add-button"
          onClick={() => setShowAddDialog(true)}
        >
          + Add New Subunit
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search subunits by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="subunits-grid">
        {filteredSubunits.length === 0 ? (
          <div className="no-subunits">
            {searchTerm ? 'No subunits found matching your search.' : 'No subunits found for this service.'}
          </div>
        ) : (
          <>
          {filteredSubunits.map((subunit) => (
            <div
              key={subunit._id}
              className={`subunit-card ${draggedSubunit?._id === subunit._id ? 'dragging' : ''} ${dragOverSubunit === subunit._id ? 'drag-over' : ''}`}
              onClick={() => openStaffs(subunit)}
              draggable={!searchTerm}
              onDragStart={(e) => handleDragStart(e, subunit)}
              onDragOver={(e) => handleDragOver(e, subunit)}
              onDrop={(e) => handleDrop(e, subunit)}
              onDragEnd={handleDragEnd}
            >
              <div className="subunit-header">
                <div className="subunit-names">
                  <h3 className="subunit-name">{subunit.name}</h3>
                  <p className="subunit-name-en">{subunit.nameEn}</p>
                </div>
                <div className="subunit-actions">
                  <div className="drag-icon-container" title={searchTerm ? 'Clear search to reorder' : 'Drag to reorder'}>
                    ⋮⋮
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(subunit);
                    }}
                    className="edit-button"
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSubunit(subunit._id);
                    }}
                    className="delete-button"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <div className="subunit-footer">
                <span className="view-staff-hint">👥 Click to view staff members</span>
              </div>
            </div>
          ))}
            {isReordering && (
              <div className="reorder-status">
                <span className="status-indicator">⏳</span>
                Updating hall order...
              </div>
            )}
          </>
        )}
      </div>

      {showAddDialog && (
        <AddServiceSubunitDialog
          onClose={() => setShowAddDialog(false)}
          onAdd={handleAddSubunit}
          serviceName={service.name}
        />
      )}

      {showEditDialog && selectedSubunit && (
        <EditServiceSubunitDialog
          subunit={selectedSubunit}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedSubunit(null);
          }}
          onEdit={handleEditSubunit}
        />
      )}
    </div>
  );
};

export default ServiceSubunits;
